import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageShell from '@/components/Layout/PageShell';
import { getErrorDetail } from '@/utils/apiError';
import {
  useListPlatformUsersQuery,
  useUpdatePlatformUserStatusMutation,
  useCreatePlatformUserMutation,
  useDeletePlatformUserMutation,
  useListPlatformTenantsQuery,
  useListPlatformRolesQuery,
  PlatformUser,
  CreatePlatformUserRequest,
} from '@/redux/internal-services/platformAdminApi';

const STATUS_BADGE: Record<string, string> = {
  active:    'bg-green-100 text-green-800',
  invited:   'bg-blue-100 text-blue-800',
  inactive:  'bg-gray-100 text-gray-600',
  suspended: 'bg-yellow-100 text-yellow-800',
  deleted:   'bg-red-100 text-red-700',
};

const STATUSES = ['All', 'active', 'invited', 'inactive', 'suspended'];

const PLATFORM_ROLES = [
  { value: 'platform_admin', label: 'Platform Admin' },
  { value: 'platform_support', label: 'Platform Support' },
];

const Avatar: React.FC<{ name?: string | null; url?: string | null }> = ({ name, url }) => {
  if (url) return <img src={url} alt={name ?? ''} className="h-8 w-8 rounded-full object-cover" />;
  const initials = (name ?? '?').split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase();
  return (
    <div className="h-8 w-8 rounded-full bg-brand-light flex items-center justify-center text-brand-dark text-xs font-semibold flex-shrink-0">
      {initials}
    </div>
  );
};

// ── Add User Modal ────────────────────────────────────────────────────────────

interface AddUserModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const AddUserModal: React.FC<AddUserModalProps> = ({ onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<CreatePlatformUserRequest>({
    name: '',
    email: '',
    password: '',
    platform_role: null,
    tenant_id: null,
    tenant_role_ids: [],
  });
  const [assignType, setAssignType] = useState<'platform' | 'tenant'>('platform');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const { data: tenantsData } = useListPlatformTenantsQuery();
  const tenants = tenantsData?.data ?? [];

  const { data: rolesData } = useListPlatformRolesQuery(
    { tenantId: form.tenant_id ?? '' },
    { skip: assignType !== 'tenant' || !form.tenant_id }
  );
  const tenantRoles = rolesData?.data ?? [];

  const [createUser, { isLoading }] = useCreatePlatformUserMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.email.trim() || !form.password.trim()) {
      setError(t('platform.userFormRequiredError'));
      return;
    }
    if (assignType === 'platform' && !form.platform_role) {
      setError(t('platform.selectPlatformRole'));
      return;
    }
    if (assignType === 'tenant' && !form.tenant_id) {
      setError(t('platform.selectTenant'));
      return;
    }
    // Resolve tenant admin role UUID for the selected tenant
    const tenantAdminRoleId = tenantRoles.find((r) =>
      r.name === 'Tenant Admin' || r.role_id === 'tenant_admin'
    )?.role_id ?? null;
    if (assignType === 'tenant' && !tenantAdminRoleId) {
      setError(t('platform.tenantAdminRoleResolutionFailed'));
      return;
    }
    try {
      await createUser({
        ...form,
        platform_role: assignType === 'platform' ? form.platform_role : null,
        tenant_id: assignType === 'tenant' ? form.tenant_id : null,
        tenant_role_ids: assignType === 'tenant' && tenantAdminRoleId ? [tenantAdminRoleId] : [],
      }).unwrap();
      onSuccess();
    } catch (err: unknown) {
      setError(getErrorDetail(err) ?? 'Failed to create user.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-ui-card rounded-2xl p-6 w-full max-w-md shadow-xl">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-ui-text">Add User</h3>
          <button onClick={onClose} className="text-ui-text3 hover:text-ui-text2 text-xl leading-none">&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-ui-text2 mb-1">Full Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full border border-ui-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-dark/40"
              placeholder="John Smith"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-ui-text2 mb-1">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full border border-ui-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-dark/40"
              placeholder="user@example.com"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-ui-text2 mb-1">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full border border-ui-border rounded-lg px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-brand-dark/40"
                placeholder="Min. 6 characters"
              />
              <button
                type="button"
                onMouseDown={() => setShowPassword(true)}
                onMouseUp={() => setShowPassword(false)}
                onMouseLeave={() => setShowPassword(false)}
                onTouchStart={() => setShowPassword(true)}
                onTouchEnd={() => setShowPassword(false)}
                className="absolute inset-y-0 right-3 flex items-center text-ui-text3 hover:text-ui-text2"
                tabIndex={-1}
                aria-label="Hold to show password"
              >
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-4 h-4">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-4 h-4">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-ui-text2 mb-2">Assign As</label>
            <div className="flex rounded-lg border border-ui-border overflow-hidden">
              {(['platform', 'tenant'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setAssignType(t)}
                  className={`flex-1 py-2 text-sm font-medium transition ${
                    assignType === t ? 'bg-brand-dark text-white' : 'bg-ui-card text-ui-text2 hover:bg-ui-hover'
                  }`}
                >
                  {t === 'platform' ? 'Platform Role' : 'Tenant Admin'}
                </button>
              ))}
            </div>
          </div>

          {assignType === 'platform' && (
            <div>
              <label className="block text-xs font-medium text-ui-text2 mb-1">Platform Role</label>
              <select
                value={form.platform_role ?? ''}
                onChange={(e) => setForm({ ...form, platform_role: e.target.value || null })}
                className="w-full border border-ui-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-dark/40"
              >
                <option value="">Select role...</option>
                {PLATFORM_ROLES.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>
          )}

          {assignType === 'tenant' && (
            <div>
              <label className="block text-xs font-medium text-ui-text2 mb-1">Tenant</label>
              <select
                value={form.tenant_id ?? ''}
                onChange={(e) => setForm({ ...form, tenant_id: e.target.value || null })}
                className="w-full border border-ui-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-dark/40"
              >
                <option value="">Select tenant...</option>
                {tenants.map((t) => (
                  <option key={t.tenant_id} value={t.tenant_id}>{t.company_name}</option>
                ))}
              </select>
              <p className="text-xs text-ui-text3 mt-1">User will be assigned as Tenant Admin.</p>
            </div>
          )}

          {error && <p className="text-red-500 text-xs">{error}</p>}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-ui-border text-ui-text py-2 rounded-lg text-sm hover:bg-ui-hover transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 bg-brand-dark text-white py-2 rounded-lg text-sm font-medium hover:bg-brand-dark/90 transition disabled:opacity-50"
            >
              {isLoading ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

const LIMIT = 50;

type SortField = 'name' | 'tenant_name' | 'status' | 'created_at';
type SortDir = 'asc' | 'desc';

const SortIcon: React.FC<{ field: SortField; current: SortField; dir: SortDir }> = ({ field, current, dir }) => {
  if (field !== current) return <span className="ml-1 text-ui-text3">↕</span>;
  return <span className="ml-1">{dir === 'asc' ? '↑' : '↓'}</span>;
};

const UsersPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [confirmUser, setConfirmUser] = useState<PlatformUser | null>(null);
  const [confirmAction, setConfirmAction] = useState<'suspend' | 'activate' | 'delete' | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [msg, setMsg] = useState<{ type: 'error' | 'success'; text: string } | null>(null);

  useEffect(() => {
    const id = setTimeout(() => { setDebounced(search.trim()); setPage(1); }, 300);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => { setPage(1); }, [status]);

  const { data, isLoading, isFetching, refetch } = useListPlatformUsersQuery(
    { search: debounced || undefined, status: status || undefined, page, limit: LIMIT },
    { refetchOnMountOrArgChange: true }
  );
  const [updateStatus, { isLoading: saving }] = useUpdatePlatformUserStatusMutation();
  const [deleteUser, { isLoading: deleting }] = useDeletePlatformUserMutation();

  const rawUsers: PlatformUser[] = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  };

  const users = [...rawUsers].sort((a, b) => {
    let av: string, bv: string;
    if (sortField === 'name') { av = (a.name ?? '').toLowerCase(); bv = (b.name ?? '').toLowerCase(); }
    else if (sortField === 'tenant_name') { av = (a.tenant_name ?? a.tenant_id ?? '').toLowerCase(); bv = (b.tenant_name ?? b.tenant_id ?? '').toLowerCase(); }
    else if (sortField === 'status') { av = a.status; bv = b.status; }
    else { av = a.created_at ?? ''; bv = b.created_at ?? ''; }
    const cmp = av < bv ? -1 : av > bv ? 1 : 0;
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const handleConfirm = async () => {
    if (!confirmUser || !confirmAction) return;
    setMsg(null);
    try {
      if (confirmAction === 'delete') {
        await deleteUser(confirmUser.user_id).unwrap();
        setMsg({ type: 'success', text: `User "${confirmUser.name ?? confirmUser.email}" has been deleted.` });
      } else {
        const newStatus = confirmAction === 'suspend' ? 'suspended' : 'active';
        await updateStatus({ userId: confirmUser.user_id, status: newStatus }).unwrap();
        setMsg({ type: 'success', text: `User status updated to "${newStatus}".` });
      }
      setConfirmUser(null);
      setConfirmAction(null);
      refetch();
    } catch (err: unknown) {
      setMsg({ type: 'error', text: getErrorDetail(err) ?? 'Action failed.' });
      setConfirmUser(null);
      setConfirmAction(null);
    }
  };

  const actionLabel = confirmAction === 'delete' ? 'Delete' : confirmAction === 'suspend' ? 'Suspend' : 'Activate';
  const actionColor = confirmAction === 'delete' ? 'bg-red-600 hover:bg-red-700' : confirmAction === 'suspend' ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-green-600 hover:bg-green-700';

  return (
    <PageShell
      maxWidth="max-w-7xl"
      header={
        <div className="space-y-3">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-2xl font-bold text-ui-text">User Management</h1>
              <p className="text-sm text-ui-text2 mt-1">View and manage all users across the platform.</p>
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center gap-2 bg-brand-dark text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-dark/90 transition"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add User
            </button>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-sm">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ui-text3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35m0 0A7 7 0 104.65 4.65a7 7 0 0012 12z" />
              </svg>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or email..."
                className="w-full pl-9 pr-4 py-2 border border-ui-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-dark/40 bg-ui-input"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {STATUSES.map((s) => (
                <button
                  key={s}
                  onClick={() => setStatus(s === 'All' ? '' : s)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition capitalize ${
                    (s === 'All' ? status === '' : status === s)
                      ? 'bg-brand-dark text-white'
                      : 'bg-ui-subtle text-ui-text2 hover:bg-ui-hover'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      }
    >

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total Users', value: total, color: 'text-brand-dark' },
          { label: 'Active', value: users.filter((u) => u.status === 'active').length, color: 'text-green-700' },
          { label: 'Invited', value: users.filter((u) => u.status === 'invited').length, color: 'text-blue-700' },
          { label: 'Inactive', value: users.filter((u) => u.status === 'inactive' || u.status === 'suspended').length, color: 'text-ui-text2' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-ui-card border border-ui-border2 rounded-xl p-4 shadow-sm">
            <p className="text-xs font-medium text-ui-text2 mb-1">{label}</p>
            <p className={`text-2xl font-bold ${color}`}>{isLoading ? '—' : value}</p>
          </div>
        ))}
      </div>

      {msg && (
        <p className={`mb-4 text-sm ${msg.type === 'error' ? 'text-red-500' : 'text-green-600'}`}>{msg.text}</p>
      )}

      {/* Table */}
      <div className="bg-ui-card border border-ui-border2 rounded-xl shadow-sm overflow-hidden">
        {isLoading || isFetching ? (
          <div className="py-20 text-center text-ui-text3 text-sm">Loading users...</div>
        ) : users.length === 0 ? (
          <div className="py-20 text-center">
            <div className="text-ui-text3 text-5xl mb-4">👤</div>
            <p className="text-ui-text3 text-sm">{t('platform.noUsersFound')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-ui-border2 bg-ui-raised">
                  <th className="text-left px-6 py-3 text-xs font-semibold text-ui-text2 uppercase tracking-wider cursor-pointer select-none hover:text-ui-text" onClick={() => toggleSort('name')}>
                    User <SortIcon field="name" current={sortField} dir={sortDir} />
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-ui-text2 uppercase tracking-wider hidden md:table-cell cursor-pointer select-none hover:text-ui-text" onClick={() => toggleSort('tenant_name')}>
                    Tenant <SortIcon field="tenant_name" current={sortField} dir={sortDir} />
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-ui-text2 uppercase tracking-wider hidden lg:table-cell">Roles</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-ui-text2 uppercase tracking-wider cursor-pointer select-none hover:text-ui-text" onClick={() => toggleSort('status')}>
                    Status <SortIcon field="status" current={sortField} dir={sortDir} />
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-ui-text2 uppercase tracking-wider hidden lg:table-cell cursor-pointer select-none hover:text-ui-text" onClick={() => toggleSort('created_at')}>
                    Joined <SortIcon field="created_at" current={sortField} dir={sortDir} />
                  </th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-ui-text2 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ui-border2">
                {users.map((user) => (
                  <tr
                    key={`${user.user_id}-${user.tenant_id}`}
                    className="hover:bg-ui-hover transition cursor-pointer"
                    onClick={() => navigate(`/platform/users/${user.user_id}`)}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <Avatar name={user.name} url={user.profile_picture_url} />
                        <div>
                          <p className="text-sm font-medium text-ui-text">{user.name ?? '—'}</p>
                          <p className="text-xs text-ui-text3">{user.email ?? '—'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 hidden md:table-cell">
                      <span className="text-sm text-ui-text2">{user.tenant_name ?? user.tenant_id ?? '—'}</span>
                    </td>
                    <td className="px-6 py-4 hidden lg:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {user.role_names.map((r) => (
                          <span key={r} className="px-2 py-0.5 bg-purple-50 text-purple-700 text-xs rounded-full border border-purple-200">{r}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${STATUS_BADGE[user.status] ?? 'bg-gray-100 text-gray-700'}`}>
                        {user.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 hidden lg:table-cell">
                      <span className="text-sm text-ui-text3">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-2">
                        {user.status === 'active' && (
                          <button
                            onClick={() => { setConfirmUser(user); setConfirmAction('suspend'); setMsg(null); }}
                            className="text-xs text-yellow-600 hover:text-yellow-800 border border-yellow-200 px-2.5 py-1 rounded transition"
                          >
                            Suspend
                          </button>
                        )}
                        {user.status === 'suspended' && (
                          <button
                            onClick={() => { setConfirmUser(user); setConfirmAction('activate'); setMsg(null); }}
                            className="text-xs text-green-600 hover:text-green-800 border border-green-200 px-2.5 py-1 rounded transition"
                          >
                            Activate
                          </button>
                        )}
                        {user.status !== 'deleted' && (
                          <button
                            onClick={() => { setConfirmUser(user); setConfirmAction('delete'); setMsg(null); }}
                            className="text-xs text-red-500 hover:text-red-700 border border-red-200 px-2.5 py-1 rounded transition"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
          <p className="text-xs text-ui-text2">
            {(page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, total)} of {total} users
          </p>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1 || isFetching}
              className="rounded-lg border border-ui-border px-3 py-1.5 text-xs font-medium text-ui-text hover:bg-ui-hover disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((n) => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
              .reduce<(number | '...')[]>((acc, n, idx, arr) => {
                if (idx > 0 && n - (arr[idx - 1] as number) > 1) acc.push('...');
                acc.push(n);
                return acc;
              }, [])
              .map((n, idx) =>
                n === '...' ? (
                  <span key={`ellipsis-${idx}`} className="px-2 text-xs text-ui-text3">…</span>
                ) : (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setPage(n as number)}
                    disabled={isFetching}
                    className={`rounded-lg border px-3 py-1.5 text-xs font-medium disabled:cursor-not-allowed ${
                      page === n
                        ? 'border-brand-dark bg-brand-dark text-white'
                        : 'border-ui-border text-ui-text hover:bg-ui-hover'
                    }`}
                  >
                    {n}
                  </button>
                ),
              )}
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || isFetching}
              className="rounded-lg border border-ui-border px-3 py-1.5 text-xs font-medium text-ui-text hover:bg-ui-hover disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {confirmUser && confirmAction && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-ui-card rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <h3 className="text-lg font-bold text-ui-text mb-2">Confirm {actionLabel}</h3>
            <p className="text-sm text-ui-text2 mb-6">
              {confirmAction === 'delete'
                ? <>Are you sure you want to <strong>delete</strong> <strong>{confirmUser.name ?? confirmUser.email}</strong>? This is a soft delete — history is preserved.</>
                : <>Are you sure you want to <strong>{actionLabel.toLowerCase()}</strong> <strong>{confirmUser.name ?? confirmUser.email}</strong>? This applies globally across all tenants.</>
              }
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => { setConfirmUser(null); setConfirmAction(null); }}
                className="flex-1 border border-ui-border text-ui-text py-2 rounded-lg text-sm hover:bg-ui-hover transition"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={saving || deleting}
                className={`flex-1 py-2 rounded-lg text-sm font-medium text-white transition disabled:opacity-50 ${actionColor}`}
              >
                {(saving || deleting) ? 'Processing...' : `Confirm ${actionLabel}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddModal && (
        <AddUserModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => { setShowAddModal(false); refetch(); setMsg({ type: 'success', text: 'User created successfully.' }); }}
        />
      )}
    </PageShell>
  );
};

export default UsersPage;
